import React, { memo, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ESRI } from '@rsos/constants/mapProviders';
import {
  GISServiceTypes,
  GISStateLayer,
  isAuthoritative,
  selectGISLayers,
} from '@rsos/reduxDataStore';
import { selectMapProvider } from '@rsos/sinatra';
import { SUPPLEMENTAL_LAYER_CATEGORIES } from '../constants';
import { GISLayerControl } from './GISLayerControl';

const KNOWN_CATEGORIES = new Set<string>(
  SUPPLEMENTAL_LAYER_CATEGORIES.map(c => c.value),
);

const isEccSpecificLayer = (layer: GISStateLayer): boolean =>
  isAuthoritative(layer.service);

const isExcludedLayer = (layer: GISStateLayer): boolean => {
  return (
    layer.service.service_type === GISServiceTypes.basemap ||
    layer.service.service_type === GISServiceTypes.geocode
  );
};

const getLayerCategory = (layer: GISStateLayer): string => {
  const category = layer.service.attributes.supplemental_layer_category;
  if (category && KNOWN_CATEGORIES.has(category)) {
    return category;
  }
  return 'other';
};

type LayerFilter = 'ecc-specific' | 'supplemental' | 'wms-wmts';

interface GISLayerControlsProps {
  /** Legacy filter by layer type (used by MapLayersControl without tabs) */
  filter?: LayerFilter;
  /** Filter by resolved category (used by MapLayersControlWithTabs) */
  categoryFilter?: string;
  /** Render only these layer ids when provided. */
  includeLayerIds?: readonly string[];
  /** Exclude these layer ids from rendering. */
  excludeLayerIds?: readonly string[];
  /** Optional remove action used by on-demand session layers. */
  onRemoveLayer?: (layerId: string) => void;
}

/**
 * Renders checkboxes for GIS layers.
 *
 * When `categoryFilter` is provided (tabbed mode), all non-excluded layers are
 * grouped by their supplemental_layer_category, defaulting to 'other' (Intelligence).
 *
 * When `filter` is provided (legacy flat-list mode), layers are filtered by type.
 *
 * Basemap and geocode layers are always excluded.
 */
export const GISLayerControls = memo<GISLayerControlsProps>(
  ({
    filter,
    categoryFilter,
    includeLayerIds,
    excludeLayerIds,
    onRemoveLayer,
  }) => {
    const mapProvider = useSelector(selectMapProvider);
    const layers = useSelector(selectGISLayers);
    const tooltip =
      mapProvider === ESRI ? undefined : 'Available with Esri Maps';

    const filteredLayers = useMemo(() => {
      const includeSet =
        includeLayerIds && includeLayerIds.length > 0
          ? new Set(includeLayerIds)
          : null;
      const excludeSet =
        excludeLayerIds && excludeLayerIds.length > 0
          ? new Set(excludeLayerIds)
          : null;

      return layers.filter(layer => {
        if (isExcludedLayer(layer)) {
          return false;
        }

        if (includeSet && !includeSet.has(layer.id)) {
          return false;
        }

        if (excludeSet && excludeSet.has(layer.id)) {
          return false;
        }

        if (categoryFilter) {
          return getLayerCategory(layer) === categoryFilter;
        }

        if (filter === 'ecc-specific') {
          return isEccSpecificLayer(layer);
        }
        if (filter === 'supplemental') {
          return (
            (layer.service.service_type === GISServiceTypes.supplemental ||
              layer.service.service_type === GISServiceTypes.wfs) &&
            !isEccSpecificLayer(layer)
          );
        }
        if (filter === 'wms-wmts') {
          return (
            layer.service.service_type === GISServiceTypes.wms ||
            layer.service.service_type === GISServiceTypes.wmts
          );
        }

        return true;
      });
    }, [layers, filter, categoryFilter, includeLayerIds, excludeLayerIds]);

    const layerControls = useMemo(
      () =>
        filteredLayers.map(l => (
          <GISLayerControl
            layer={l}
            key={l.id}
            onRemove={onRemoveLayer ? () => onRemoveLayer(l.id) : undefined}
          />
        )),
      [filteredLayers, onRemoveLayer],
    );

    if (filteredLayers.length === 0) {
      return null;
    }

    return <div title={tooltip}>{layerControls}</div>;
  },
);
